<template>
  <div class="LKPublicationsStorage">
    <LKPublicationsStorageHeader
      :data="data"
      :publication-id="publicationId"
      class="LKPublicationsStorage__header"
      @downloaded="$emit('downloaded', data, $event)"
    />

    <BListFilter
      v-if="isActive || filters.search"
      v-model="filters"
      label="Поиск файла по названию"
      dense
      @change="onChangeFilter"
    />

    <template v-if="isLoadingAsyncData">
      <v-layout row wrap>
        <v-flex v-for="i in 6" :key="i" xs4 style="padding: 16px;">
          <v-skeleton-loader type="article" />
        </v-flex>
      </v-layout>
    </template>

    <template v-else-if="isContainerVisible">
      <v-row
        dense
        :class="{ 'is-active': isActive, 'has-toggle': hasToggle }"
        :style="!isActive && toggleStyle"
        class="LKPublicationsStorage__toggle-container"
      >
        <template v-for="item in list">
          <v-col :key="item.id" cols="6" md="4" class="js-toggle">
            <LKPublicationsStorageFileBox
              :data="item"
              :publication-id="publicationId"
              :storage="data"
            />
          </v-col>
        </template>

        <template v-if="totalFilesCount > DEFAULT_PER_PAGE">
          <v-container class="max-width my-4">
            <v-pagination
              v-model="pagination.page"
              color="#4A759D"
              :length="paginationLength"
              :total-visible="10"
            />
          </v-container>
        </template>
      </v-row>
    </template>

    <template v-else-if="list && list.length === 0">
      <span class="LKPublicationsStorage__no-messages-text">Нет доступных файлов</span>
    </template>

    <template v-if="errorMessage">
      <div
        class="error--text ml-8"
        v-text="errorMessage"
      />
      <v-btn
        dark
        block
        depressed
        :color="color"
        @click.stop="getData"
        v-text="'Повторить'"
      />
    </template>

    <div v-if="hasToggle" class="LKPublicationsStorage__toggle">
      <button
        class="LKPublicationsStorage__toggle-button"
        @click="isActive = !isActive"
      >
        {{ isActive ? 'Свернуть' : 'Раскрыть' }}
        <v-icon
          size="20px"
          :class="{ 'mdi-rotate-180': isActive }"
          class="LKPublicationsStorage__toggle-icon"
          v-text="'mdi-chevron-down'"
        />
      </button>
    </div>
  </div>
</template>

<script>
import BListFilter from '@/components/forms/BListFilter.vue'
import LKPublicationsStorageHeader from './LKPublicationsStorageHeader.vue'
import LKPublicationsStorageFileBox from './LKPublicationsStorageFileBox.vue'
import { lkPublicationsMapper as StorageMapper } from '@/views/LKPublications/store'
import PublicationsCardComponentMixin
  from '@/views/LKPublications/components/LKPublicationsCard/PublicationsCardComponentMixin'
import { LkPublicationsApi } from '@/api/resources'


const DEFAULT_PER_PAGE = 12

export default {
  name: 'LKPublicationsStorage',
  components: {
    BListFilter,
    LKPublicationsStorageHeader,
    LKPublicationsStorageFileBox,
  },
  mixins: [PublicationsCardComponentMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    DEFAULT_PER_PAGE,
    isActive: false,
    toggleStyle: {},
    filters: {},
    pagination: {
      page: 1,
      per_page: DEFAULT_PER_PAGE,
    },
    filesCount: null,
    totalFilesCount: null,
  }),
  computed: {
    hasToggle() {
      if (this.list?.length === 0) return false
      if (this.filters.search) return true
      const { mdAndUp } = this.$vuetify.breakpoint

      return mdAndUp
        ? this.totalFilesCount > 6
        : this.totalFilesCount > 4
    },
    paginationLength() {
      return Math.floor(this.filesCount / DEFAULT_PER_PAGE) + 1
    },
  },
  watch: {
    'pagination.page': {
      handler() {
        this.getData()
      },
    },
    list: {
      handler() {
        this.$nextTick(this.setStyles)
      },
    },
    active: {
      handler(v) {
        if (!v) return
        this.loadStorageFilesCount()
        this.getData()
      },
      immediate: true,
    },
    '$vuetify.breakpoint.width': {
      handler: 'setStyles',
    },
  },
  mounted() {
    this.setStyles()
    if (this.data?.count > (DEFAULT_PER_PAGE - 1)) {
      this.loadStorageFilesCount()
    }
  },
  methods: {
    ...StorageMapper.mapActions(['getStorageFilesCount']),
    async loadData() {
      const params = {
        ...this.pagination,
        ...this.filters,
      }

      const api = new LkPublicationsApi()
      const data = await api.getStorageFiles(this.publicationId, this.data.id, params)

      this.filesCount = data?.count || 0

      return data?.data
    },
    async loadStorageFilesCount() {
      const { publicationId, data } = this
      const count = await this.getStorageFilesCount({ publicationId, storageId: data.id })
      this.filesCount = count
      this.totalFilesCount = count
    },
    async onChangeFilter(_, callback) {
      this.pagination.page = 1

      const unwatch = callback?.()

      await this.getData()

      unwatch?.()
    },
    async getData() {
      if (!this.publicationId) return

      this.isLoadingAsyncData = true
      this.errorMessage = null

      try {
        this.list = await this.loadData()
      } catch (e) {
        this.errorMessage = e.message
      }

      this.isLoadingAsyncData = false
    },
    setStyles() {
      const list = [...this.$el.querySelectorAll('.js-toggle')]
      const line1 = list.slice(0, 3).map((el) => el.offsetHeight)
      const max1 = line1.length && Math.max(...line1)

      const line2 = list.slice(3, 6).map((el) => el.offsetHeight)
      const max2 = line2.length && Math.max(...line2)

      const maxHeight = `${max1 + max2}px`
      this.toggleStyle = this.hasToggle ? { maxHeight } : void 0
    },
  },
}
</script>

<style lang="scss">
$max-height: 140px;

.LKPublicationsStorage {
  position: relative;
  padding: 18px 22px 25px 22px;
  background-color: $webflow-color-blue-shadow;

  .v-skeleton-loader {
    &__article,
    &__actions {
      background: $webflow-color-blue-light-2 !important;
    }
  }

  &__header {
    margin-bottom: 19px;
  }

  &__no-messages-text {
    padding: 4px;
    font-size: 14px;
  }

  &__toggle-container {
    position: relative;
    // max-height: $max-height;
    overflow: hidden;

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      display: none;
      pointer-events: none;
      content: "";
      background:
        rgba($webflow-color-blue-shadow, 0.2)
        linear-gradient(
          180deg,
          rgba($webflow-color-blue-shadow, 0),
          rgba($webflow-color-blue-shadow, 0.3),
          rgba($webflow-color-blue-shadow, 0.5),
          rgba($webflow-color-blue-shadow, 1)
        );
    }

    &.has-toggle {
      &::before {
        display: block;
      }
    }

    &.is-active {
      max-height: none;

      &::before {
        display: none;
      }
    }
  }

  &__toggle {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    border-bottom: 2px solid $webflow-color-blue-dark;
    transition: border-bottom 0.2s;
  }

  &__toggle-icon {
    color: inherit !important;
  }

  &__toggle-button {
    z-index: 5;
    padding: 2px 16px;
    color: $webflow-color-white;
    background-color: $webflow-color-blue-dark;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    outline-style: none;

    &:hover {
      background-color: $webflow-color-blue-dark-3;
    }
  }
}
</style>
